import { useCallback, useEffect, useRef } from "react";

/** Debounce delay (ms) before a server-side search is delegated to the parent. */
export const FOLDER_SEARCH_DEBOUNCE_MS = 350;

/**
 * Returns a debounced search-change handler that delegates filtering to the parent
 * (server-side `?search=`) instead of filtering the loaded blocks client-side.
 * Keeps the local ref in sync so `useBlocksSync` can re-apply the term on batch updates.
 */
export function useServerSearchHandler(
	onServerSearch: ((term: string) => void) | undefined,
	searchValueRef: React.MutableRefObject<string>,
): (value: string) => void {
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handler = useCallback(
		(value: string): void => {
			searchValueRef.current = value;
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout((): void => {
				onServerSearch?.(value);
			}, FOLDER_SEARCH_DEBOUNCE_MS);
		},
		[onServerSearch, searchValueRef],
	);

	useEffect((): (() => void) => {
		return (): void => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	return handler;
}
